/**
 * M3C-04 — small fetch helper for the `workflows/v1` namespace (the tenant's
 * own workflows + the injection nodes shared with it; DI-D62 step 0).
 *
 * The `projectsFetch.ts` pattern: the surface is TENANT-SCOPED and read from
 * the caller's OWN tenant (workflows/v1 never takes the workflow-share
 * exception), so every call sends the auth store's `X-Tenant-ID`. A per-set
 * override does NOT exist here — that lives in the intake's two fetch helpers
 * only (lock `one-header-place`). Base URL from `getWorkflowsBaseUrl()` so
 * `DEV_API_MODE` routes correctly.
 */
import { getWorkflowsBaseUrl, useAuthStore } from "@aeriosiq/auth-client";

export interface WorkflowsFetchError extends Error {
  status: number;
  body: unknown;
}

function authHeaders(): Headers {
  const session = useAuthStore.getState().session;
  const tenant = useAuthStore.getState().tenant;
  const headers = new Headers();
  if (session?.accessToken) {
    headers.set("Authorization", `Bearer ${session.accessToken}`);
  }
  if (tenant?.tenantSlug) {
    headers.set("X-Tenant-ID", tenant.tenantSlug);
  }
  return headers;
}

export async function workflowsFetch<T>(path: string): Promise<T> {
  const res = await fetch(`${getWorkflowsBaseUrl()}${path}`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!res.ok) {
    let parsedBody: unknown = null;
    try {
      parsedBody = await res.json();
    } catch {
      // Non-JSON error body — the status still surfaces.
    }
    const err = new Error(
      `workflows fetch failed: ${path} → ${String(res.status)}`,
    ) as WorkflowsFetchError;
    err.status = res.status;
    err.body = parsedBody;
    throw err;
  }
  return (await res.json()) as T;
}
